import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { SmallArrowDownIcon, SearchIcon, EditIcon, DeleteIcon } from '../Icons'
import StatusToggle from '../ui/StatusToggle'
import { Eye } from 'lucide-react'
import ScoreDisplay from '../ui/ScoreDisplay'
import ConfirmModal from '../ui/ConfirmModal'
import EditStudent from './EditStudent'
import ViewStudent from './ViewStudent'

function StudentList() {
  const location = useLocation()
  const [parentData, setParentData] = useState([])
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [entriesCount, setEntriesCount] = useState(200)
  const [currentPage, setCurrentPage] = useState(1)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalAction, setStatusModalAction] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [studentToDelete, setStudentToDelete] = useState(null)
  const [showEditStudentModal, setShowEditStudentModal] = useState(false)
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [showViewStudentModal, setShowViewStudentModal] = useState(false)
  const [selectedViewStudentId, setSelectedViewStudentId] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getParents()
        await getStudents()
        setIsLoading(false)
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false)
      }
    }
    fetchData()
    // eslint-disable-next-line
  }, [])

  const parent_id = new URLSearchParams(location.search).get('parent_id')
  const batch_id = new URLSearchParams(location.search).get('batch_id')

  const getParents = async () => {
    let url = `${process.env.REACT_APP_BASE_URL}getallparents`
    try {
      const response = await axios.get(url)
      if (response.status === 200) {
        setParentData(response.data)
      }
    } catch (error) {
      console.error('Error fetching parents:', error)
    }
  }

  const getStudents = async () => {
    let url = `${process.env.REACT_APP_BASE_URL}getallstudents`
    if (parent_id !== null) {
      url = `${process.env.REACT_APP_BASE_URL}getallstudents?parent_id=${parent_id}`
    } else if (batch_id !== null) {
      url = `${process.env.REACT_APP_BASE_URL}getallstudents?batch_id=${batch_id}`
    }
    try {
      const response = await axios.get(url)
      if (response.status === 200) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error fetching students:', error)
    }
  }

  const deleteStudent = async (id) => {
    const response = await axios.delete(
      `${process.env.REACT_APP_BASE_URL}delete_user/${id}`
    )
    toast.success(response.data)
    getStudents()
    window.location.reload(true)
  }

  const blockStudent = async (id) => {
    const response = await axios.put(
      `${process.env.REACT_APP_BASE_URL}block_user/${id}`
    )
    toast.success(response.data)
    getStudents()
    window.location.reload(true)
  }

  const unblockStudent = async (id) => {
    const response = await axios.put(
      `${process.env.REACT_APP_BASE_URL}unblock_user/${id}`
    )
    toast.success(response.data)
    getStudents()
  }

  const handleStatusToggle = (id, currentStatus) => {
    setStatusModalAction({ id, type: currentStatus ? 'block' : 'unblock' })
    setStatusModalOpen(true)
  }

  const handleDeleteClick = (id) => {
    setStudentToDelete(id)
    setDeleteModalOpen(true)
  }

  // Filtered and paginated data
  const filteredData = data.filter((item) => {
    const parent = parentData.find((parent) => parent._id === item.parent_id)
    return (
      (
        item.first_name +
        ' ' +
        (item.middle_name || '') +
        ' ' +
        (item.last_name || '')
      )
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (parent &&
        (parent.first_name + ' ' + (parent.last_name || ''))
          .toLowerCase()
          .includes(searchTerm.toLowerCase()))
    )
  })
  const indexOfLastEntry = currentPage * entriesCount
  const indexOfFirstEntry = indexOfLastEntry - entriesCount
  const paginatedData = filteredData.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(filteredData.length / entriesCount)

  // Handler for page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  // Reset to page 1 when entriesCount or searchTerm changes
  useEffect(() => {
    setCurrentPage(1)
  }, [entriesCount, searchTerm])

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      {/* Header Controls - Match ParentList UX */}
      <div className="justify-between items-center border border-[#F0F0F0] flex w-full gap-[40px_100px] font-medium leading-[1.2] flex-wrap bg-white px-5 py-4 rounded-t-[10px]">
        <h2 className="text-lg text-[#030303] font-bold">Student List</h2>
        <div className="self-stretch flex min-w-60 items-center gap-2.5 text-xs text-[#535862] flex-wrap w-[750px] my-auto max-md:max-w-full">
          <div className="self-stretch flex items-center gap-[9px] my-auto">
            <div className="text-[#535862] self-stretch my-auto">
              Show Entries
            </div>
            <div className="self-stretch flex items-center gap-2 whitespace-nowrap text-center w-[90px] my-auto relative">
              <div className="justify-center items-stretch border border-[#F0F0F0] self-stretch flex w-[90px] flex-col bg-white my-auto px-2.5 py-[9px] rounded-[10px] border-solid relative">
                <select
                  className="text-[#535862] self-stretch my-auto bg-transparent outline-none appearance-none w-full pr-6"
                  value={entriesCount}
                  onChange={(e) => setEntriesCount(Number(e.target.value))}
                  aria-label="Entries per page"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                  }}
                >
                  {[200, 400, 600].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 flex w-4 h-4">
                  <SmallArrowDownIcon />
                </div>
              </div>
            </div>
          </div>
          <div className="self-stretch min-w-60 text-sm font-normal grow shrink w-[356px] my-auto rounded-[0px_0px_0px_0px]">
            <div className="flex items-center gap-1 border border-[#F0F0F0] bg-white px-3 py-2 rounded-md w-full focus-within:border-[#DE3C7D]">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search student..."
                className="text-[#535862] bg-transparent outline-none border-none w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <Link
            to={`/parents`}
            className="justify-center items-stretch self-stretch flex flex-col text-white text-center grow shrink w-[150px] bg-[#DE3C7D] my-auto p-2.5 rounded-md flex-shrink-0"
          >
            <div className="flex w-full gap-1 items-center justify-center">
              <div className="text-white">Back to Parents</div>
            </div>
          </Link>
        </div>
      </div>
      {/* Table Header - Match ParentList UX */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neutral-50">
            <tr>
              <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                S.No
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                Student Name
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-[280px] px-5 py-3.5 border-[#F0F0F0]">
                Email Address
              </th>
              <th className="text-sm text-[#535862] font-medium text-left border w-60 px-5 py-3.5 border-[#F0F0F0]">
                Parent Name
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-24 px-2 py-3.5 border-[#F0F0F0]"
                colSpan={2}
              >
                ACT
              </th>
              <th
                className="text-sm text-[#535862] font-medium text-center border w-24 px-2 py-3.5 border-[#F0F0F0]"
                colSpan={2}
              >
                SAT
              </th>
              <th className="text-sm text-[#535862] font-medium text-center border w-24 px-5 py-3.5 border-[#F0F0F0]">
                Status
              </th>
              <th className="text-sm text-[#535862] font-medium text-center px-5 py-3.5 border border-[#F0F0F0]">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="border border-[#F0F0F0]">
                  <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </td>
                  <td className="px-5 py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-8"></div>
                    </div>
                  </td>
                </tr>
              ))
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item, index) => {
                const parent = parentData.find(
                  (parent) => parent._id === item.parent_id
                )
                return (
                  <tr key={item._id} className="border border-[#F0F0F0]">
                    <td className="text-sm text-[#030303] text-center border px-5 py-2 border-[#F0F0F0]">
                      {(index + 1).toString().padStart(2, '0')}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0] font-semibold">
                      {item.first_name} {item.middle_name} {item.last_name}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      {item.email}
                    </td>
                    <td className="text-sm text-[#030303] border px-5 py-2 border-[#F0F0F0]">
                      {parent
                        ? `${parent.first_name} ${parent.last_name}`
                        : 'Unknown'}
                    </td>
                    <td className="text-sm text-[#030303] text-center border w-14 px-2 py-2 border-[#F0F0F0]">
                      <Link to={`/students_acts?student_id=${item._id}`}>
                        <ScoreDisplay score={item.act_count} type="ACT" />
                      </Link>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0]">
                      <button
                        className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                        title="Assign ACT Tests"
                        onClick={() =>
                          navigate(`/assignacttestsstudents/${item._id}`)
                        }
                      >
                        <span className="text-[#535862] text-lg font-bold">
                          +
                        </span>
                      </button>
                    </td>
                    <td className="text-sm text-[#030303] text-center border w-14 px-2 py-2 border-[#F0F0F0]">
                      <Link to={`/students_sats?student_id=${item._id}`}>
                        <ScoreDisplay score={item.sat_count} type="SAT" />
                      </Link>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0]">
                      <button
                        className="justify-center items-center border border-[#F0F0F0] flex min-h-8 w-9 bg-neutral-50 px-2 py-1.5 rounded-[10px] border-solid hover:bg-neutral-100"
                        title="Assign SAT Tests"
                        onClick={() =>
                          navigate(`/assignsattestsstudents/${item._id}`)
                        }
                      >
                        <span className="text-[#535862] text-lg font-bold">
                          +
                        </span>
                      </button>
                    </td>
                    <td className="text-center border w-12 px-2 py-2 border-[#F0F0F0]">
                      <div className="flex justify-center">
                        <StatusToggle
                          status={item.status}
                          onChange={() =>
                            handleStatusToggle(item._id, item.status)
                          }
                        />
                      </div>
                    </td>
                    <td className="px-5 py-2">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          className="justify-center items-center border border-[color:var(--Stock,#F0F0F0)] self-stretch flex min-h-8 flex-col w-9 bg-neutral-50 my-auto px-2 py-1.5 rounded-[10px] border-solid"
                          title="View"
                          onClick={() => {
                            setSelectedViewStudentId(item._id)
                            setShowViewStudentModal(true)
                          }}
                        >
                          <Eye className="w-5 h-5 text-[#535862]" />
                        </button>
                        <button
                          className="border bg-neutral-50 p-2 rounded-[10px] border-[#F0F0F0] hover:bg-neutral-100 transition-colors"
                          title="Edit"
                          onClick={() => {
                            setSelectedStudentId(item._id)
                            setShowEditStudentModal(true)
                          }}
                        >
                          <span className="text-[#535862]">
                            <EditIcon />
                          </span>
                        </button>
                        <button
                          className="border bg-[rgba(233,0,4,0.10)] p-2 rounded-[10px] border-[rgba(233,0,4,0.20)] hover:bg-[rgba(233,0,4,0.15)] transition-colors"
                          title="Delete"
                          onClick={() => handleDeleteClick(item._id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-8 text-[#535862]">
                  No students found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination Controls */}
        {filteredData.length > 0 && (
          <div className="flex justify-between items-center mt-4 px-4">
            <div className="text-sm text-[#535862]">
              Showing {filteredData.length === 0 ? 0 : indexOfFirstEntry + 1} to{' '}
              {Math.min(indexOfLastEntry, filteredData.length)} of{' '}
              {filteredData.length} entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-3 py-1 border border-[#F0F0F0] rounded-md ${
                    currentPage === index + 1 ? 'bg-[#DE3C7D] text-white' : ''
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-[#F0F0F0] rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
      <ConfirmModal
        open={statusModalOpen}
        title={
          statusModalAction?.type === 'block'
            ? 'Deactivate Student'
            : 'Activate Student'
        }
        message={
          statusModalAction?.type === 'block'
            ? 'Are you sure you want to deactivate this student?'
            : 'Are you sure you want to activate this student?'
        }
        onCancel={() => {
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
        onConfirm={async () => {
          if (statusModalAction) {
            if (statusModalAction.type === 'block') {
              await blockStudent(statusModalAction.id)
            } else {
              await unblockStudent(statusModalAction.id)
            }
          }
          setStatusModalOpen(false)
          setStatusModalAction(null)
        }}
      />
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Student"
        message="Are you sure you want to delete this student? This action cannot be undone."
        onCancel={() => {
          setDeleteModalOpen(false)
          setStudentToDelete(null)
        }}
        onConfirm={async () => {
          if (studentToDelete) {
            await deleteStudent(studentToDelete)
          }
          setDeleteModalOpen(false)
          setStudentToDelete(null)
        }}
      />
      {/* Edit Student Modal */}
      {showEditStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] w-[800px] max-h-[90vh] overflow-y-auto">
            <EditStudent
              onClose={() => setShowEditStudentModal(false)}
              studentId={selectedStudentId}
              onSave={async () => {
                setShowEditStudentModal(false)
                setSelectedStudentId(null)
                await getStudents()
              }}
            />
          </div>
        </div>
      )}
      {/* View Student Modal */}
      {showViewStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-[20px] w-[800px] max-h-[90vh] overflow-y-auto">
            <ViewStudent
              onClose={() => setShowViewStudentModal(false)}
              studentId={selectedViewStudentId}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default StudentList
